import { useCallback, useRef } from 'react';
import React from 'react';

// Create the context outside the hook so it can be imported directly
export const ScrollContext = React.createContext({
  scrollToElement: () => {},
  containerRef: null
});

/**
 * Hook that provides scrolling functionality for highlighted elements
 * @param {number} headerOffset - Optional offset for the header (default: 300px)
 * @returns {Object} - Object containing scrollToElement function
 */
export function useScrollToHighlight(headerOffset = 300) {
  // Create a ref for the scrollable container
  const scrollContainerRef = useRef(null);
  
  // Scroll function implementation
  const scrollToElement = useCallback((element) => {
    if (!element) return;
    
    // Find all scrollable parents
    const scrollableParents = [];
    let parent = element.parentElement;
    
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflow = style.getPropertyValue('overflow-y');
      const isScrollable = (overflow === 'auto' || overflow === 'scroll') && 
                          parent.scrollHeight > parent.clientHeight;
    
      if (isScrollable) {
        scrollableParents.push(parent);
      }
      parent = parent.parentElement;
    }
    
    // If we found scrollable parents, scroll the first/closest one
    if (scrollableParents.length > 0) {
      const scrollContainer = scrollableParents[0];
      
      // Get the relative position within the scrollable container
      let offsetTop = 0;
      let currentEl = element;
      
      while (currentEl && currentEl !== scrollContainer) {
        offsetTop += currentEl.offsetTop;
        currentEl = currentEl.offsetParent;
      }
      
      // Position the element near the top with header offset
      const scrollPosition = Math.max(0, offsetTop - headerOffset);
      
      // Apply the scroll with smooth behavior
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [headerOffset]);
  
  return {
    scrollToElement,
    scrollContainerRef
  };
}

export default useScrollToHighlight; 